<template>
    <teleport to="body">
        <div v-if="isOpen" class="modal-overlay" @click="closeModal">
            <div class="modal-container" @click.stop>
                <div class="modal-header">
                    <h3 class="modal-title">
                        {{ t('moderation.report_message') }}
                    </h3>
                    <button class="close-button" @click="closeModal">×</button>
                </div>

                <div class="modal-body">
                    <div v-if="!isSubmitted" class="report-dialog">
                        <!-- Select categories -->
                        <div class="field my-4">
                            <label for="categories">{{
                                t('moderation.report_categories')
                            }}</label>
                            <Dropdown
                                v-model="selectedCategory"
                                :options="categoryOptions"
                                optionLabel="name"
                                :placeholder="t('moderation.select_category')"
                                class="w-full mt-1"
                            />
                        </div>

                        <!-- Optional note -->
                        <div class="field my-4">
                            <label for="note">{{ t('moderation.note') }}</label>
                            <Textarea
                                v-model="note"
                                :placeholder="notePlaceholder"
                                class="w-full mt-1"
                                rows="5"
                            />
                        </div>

                        <!-- DMCA specific fields -->
                        <div
                            v-if="isDepictedWithoutConsentSelected"
                            class="field my-4"
                        >
                            <div class="flex align-items-center">
                                <Checkbox
                                    v-model="goodFaith"
                                    binary
                                    inputId="goodFaith"
                                />
                                <label for="goodFaith" class="ml-2">{{
                                    t('moderation.dmca_good_faith')
                                }}</label>
                            </div>
                        </div>
                        <div
                            v-if="isDepictedWithoutConsentSelected"
                            class="field my-4"
                        >
                            <div class="flex align-items-center">
                                <Checkbox
                                    v-model="perjury"
                                    binary
                                    inputId="perjury"
                                />
                                <label for="perjury" class="ml-2">{{
                                    t('moderation.dmca_penalty_of_perjury')
                                }}</label>
                            </div>
                        </div>
                        <div
                            v-if="isDepictedWithoutConsentSelected"
                            class="field my-4"
                        >
                            <label for="legalName">{{
                                t('moderation.full_legal_name')
                            }}</label>
                            <InputText
                                id="legalName"
                                v-model="legalName"
                                :placeholder="
                                    t('moderation.full_legal_name_placeholder')
                                "
                                class="w-full mt-1"
                            />
                        </div>

                        <!-- Attachment -->
                        <div class="field my-4">
                            <label for="attachment">
                                {{
                                    isDepictedWithoutConsentSelected
                                        ? t('moderation.attach_image_id')
                                        : t('moderation.attach_image')
                                }}
                            </label>
                            <div
                                class="attachment-upload-container mt-1"
                                @click="triggerFileUpload"
                                @dragover.prevent
                                @drop.prevent="handleFileDrop"
                            >
                                <input
                                    type="file"
                                    ref="fileInput"
                                    @change="handleFileSelect"
                                    accept="image/*"
                                    class="hidden-file-input"
                                />
                                <div
                                    v-if="!attachmentPreview"
                                    class="upload-placeholder"
                                >
                                    <i class="pi pi-upload text-2xl mb-2"></i>
                                    <p>{{ t('moderation.upload_prompt') }}</p>
                                </div>
                                <div v-else class="image-preview-container">
                                    <img
                                        :src="attachmentPreview"
                                        class="image-preview"
                                        alt="Attachment Preview"
                                    />
                                    <button
                                        class="remove-image-button"
                                        @click.stop="removeAttachment"
                                    >
                                        ×
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Optional email -->
                        <div v-if="showEmailField" class="field my-4">
                            <label for="email">{{
                                t('moderation.contact_email')
                            }}</label>
                            <InputText
                                id="email"
                                v-model="email"
                                :placeholder="
                                    t('moderation.contact_email_placeholder')
                                "
                                class="w-full mt-1"
                            />
                        </div>
                    </div>

                    <div v-else class="thank-you-message">
                        <i
                            class="pi pi-check-circle text-4xl text-green-500 mb-3"
                        ></i>
                        <h3>{{ t('moderation.report_submitted') }}</h3>
                        <p>{{ t('moderation.report_thank_you') }}</p>
                    </div>
                </div>

                <div class="modal-footer">
                    <div
                        v-if="!isSubmitted"
                        class="flex justify-content-end gap-2"
                    >
                        <button class="cancel-button" @click="closeModal">
                            {{ t('general.cancel') }}
                        </button>
                        <button
                            class="confirm-button"
                            @click="submitReport"
                            :disabled="isSubmitDisabled"
                        >
                            <span
                                v-if="isSubmitting"
                                class="loading-spinner"
                            ></span>
                            <span>{{ t('moderation.send_report') }}</span>
                        </button>
                    </div>
                    <div v-else class="flex justify-content-center">
                        <button class="confirm-button" @click="closeModal">
                            {{ t('general.confirm') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import Dropdown from 'primevue/dropdown'
import Textarea from 'primevue/textarea'
import InputText from 'primevue/inputtext'
import Checkbox from 'primevue/checkbox'
import { useI18n } from 'vue-i18n'
import moderationService from '@/services/api/moderation.service'
import { useAuthStore } from '@/stores/auth.store'
import { uploadImage } from '@/utils/media.js'
import { useToast } from 'primevue/usetoast'

const { t } = useI18n()
const authStore = useAuthStore()
const toast = useToast()

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false,
    },
    messageId: {
        type: String,
        default: null,
    },
})

const emit = defineEmits(['close', 'reported'])

const isGenericReport = computed(() => !props.messageId)

const selectedCategory = ref(null)
const note = ref('')
const email = ref('')
// NEW DMCA-specific refs
const goodFaith = ref(false)
const perjury = ref(false)
const legalName = ref('')
const isSubmitting = ref(false)
const isSubmitted = ref(false)
const attachmentFile = ref(null)
const attachmentPreview = ref(null)
const fileInput = ref(null)

const notePlaceholder = computed(() => {
    if (selectedCategory.value?.id === 'depicted_without_consent') {
        return t('moderation.note_placeholder_depicted_without_consent')
    }
    return t('moderation.note_placeholder_generic')
})

const categoryOptions = computed(() => {
    if (isGenericReport.value) {
        return [
            {
                id: 'technical_issue',
                name: t('moderation.category_technical_issue'),
            },
            {
                id: 'billing_problem',
                name: t('moderation.category_billing_problem'),
            },
            {
                id: 'account_access',
                name: t('moderation.category_account_access'),
            },
            {
                id: 'inappropriate_content',
                name: t('moderation.category_inappropriate_content'),
            },
            { id: 'scam_fraud', name: t('moderation.category_scam_fraud') },
            {
                id: 'depicted_without_consent',
                name: t('moderation.category_depicted_without_consent'),
            },
            { id: 'other', name: t('moderation.category_other') },
        ]
    }
    return [
        { id: 'spam', name: t('moderation.category_spam') },
        { id: 'inappropriate', name: t('moderation.category_inappropriate') },
        { id: 'harassment', name: t('moderation.category_harassment') },
        { id: 'violence', name: t('moderation.category_violence') },
        { id: 'scam', name: t('moderation.category_scam') },
        {
            id: 'depicted_without_consent',
            name: t('moderation.category_depicted_without_consent'),
        },
        { id: 'other', name: t('moderation.category_other') },
    ]
})

// NEW computed helpers
const isDepictedWithoutConsentSelected = computed(
    () => selectedCategory.value?.id === 'depicted_without_consent'
)

const showEmailField = computed(() => {
    return (
        isDepictedWithoutConsentSelected.value ||
        (isGenericReport.value && !authStore.userId)
    )
})

const isSubmitDisabled = computed(() => {
    // Base checks
    if (!selectedCategory.value || isSubmitting.value) return true

    // DMCA special requirements
    if (isDepictedWithoutConsentSelected.value) {
        return (
            !goodFaith.value ||
            !perjury.value ||
            legalName.value.trim() === '' ||
            email.value.trim() === '' ||
            !attachmentFile.value // Attachment mandatory
        )
    }

    // Generic report requires note
    if (isGenericReport.value) {
        return note.value.trim() === ''
    }

    return false
})

watch(
    () => props.isOpen,
    (newValue) => {
        if (newValue) {
            resetForm()
        }
    }
)

const submitReport = async () => {
    if (isSubmitDisabled.value) return

    try {
        isSubmitting.value = true
        let attachmentUrl = null

        if (attachmentFile.value) {
            attachmentUrl = await uploadImage(
                attachmentFile.value,
                true,
                toast,
                t
            )
            if (!attachmentUrl) {
                // Show error toast if upload fails, but don't block the report
                toast.add({
                    severity: 'error',
                    summary: t('general.upload-error'),
                    detail: t('moderation.report_sent_without_image'),
                    life: 5000,
                })
            }
        }

        const categoryIds = [selectedCategory.value.id]

        // Compose final note, including DMCA statements if applicable
        let finalNote = note.value
        if (isDepictedWithoutConsentSelected.value) {
            finalNote =
                `DMCA Takedown Declaration:\n` +
                `Good Faith: ${goodFaith.value ? 'true' : 'false'}\n` +
                `Penalty of Perjury: ${perjury.value ? 'true' : 'false'}\n` +
                `Signature: ${legalName.value}\n` +
                `${note.value}`

            // Attachment is mandatory, if upload failed abort submission
            if (!attachmentUrl) {
                toast.add({
                    severity: 'error',
                    summary: t('general.error'),
                    detail: t('moderation.attachment_required'),
                    life: 5000,
                })
                return
            }
        }

        if (isGenericReport.value) {
            await moderationService.reportGeneric(
                categoryIds,
                finalNote,
                email.value,
                attachmentUrl
            )
        } else {
            await moderationService.reportMessage(
                props.messageId,
                categoryIds,
                finalNote,
                attachmentUrl
            )
        }

        isSubmitted.value = true
        emit('reported', props.messageId)
    } catch (error) {
        let errorMessage = 'Error submitting report'
        if (isGenericReport.value) {
            errorMessage = 'Error submitting generic report:'
        }
        console.error(errorMessage, error)
        toast.add({
            severity: 'error',
            summary: t('general.error'),
            detail: t('general.error-something-went-wrong'),
            life: 3000,
        })
    } finally {
        isSubmitting.value = false
    }
}

const resetForm = () => {
    selectedCategory.value = null
    note.value = ''
    email.value = ''
    goodFaith.value = false
    perjury.value = false
    legalName.value = ''
    isSubmitted.value = false
    removeAttachment()
}

const closeModal = () => {
    emit('close')
}

const triggerFileUpload = () => {
    fileInput.value.click()
}

const handleFileSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
        setAttachment(file)
    }
}

const handleFileDrop = (event) => {
    const file = event.dataTransfer.files[0]
    if (file && file.type.startsWith('image/')) {
        setAttachment(file)
    }
}

const setAttachment = (file) => {
    attachmentFile.value = file
    attachmentPreview.value = URL.createObjectURL(file)
}

const removeAttachment = () => {
    attachmentFile.value = null
    if (attachmentPreview.value) {
        URL.revokeObjectURL(attachmentPreview.value)
        attachmentPreview.value = null
    }
    if (fileInput.value) {
        fileInput.value.value = ''
    }
}
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
}

.modal-container {
    background-color: #0d1038;
    border-radius: 8px;
    width: 90%;
    max-width: 500px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid #1d2452;
}

.modal-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: white;
}

.close-button {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 4px;
}

.hidden-file-input {
    display: none;
}

.attachment-upload-container {
    border: 2px dashed #3f4a7b;
    border-radius: 8px;
    padding: 20px;
    text-align: center;
    cursor: pointer;
    transition: background-color 0.2s;
    position: relative;
}

.attachment-upload-container:hover {
    background-color: #1d2452;
}

.upload-placeholder i {
    color: #6c757d;
}

.upload-placeholder p {
    margin: 0;
    color: #adb5bd;
}

.image-preview-container {
    position: relative;
    display: inline-block;
}

.image-preview {
    max-width: 100%;
    max-height: 150px;
    border-radius: 4px;
}

.remove-image-button {
    position: absolute;
    top: -10px;
    right: -10px;
    background-color: #0d1038;
    color: white;
    border: 1px solid white;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    cursor: pointer;
}

.close-button:hover {
    background-color: rgba(255, 255, 255, 0.1);
}

.modal-body {
    padding: 16px 20px;
    color: white;
    max-height: 60vh; /* New: limit height */
    overflow-y: auto; /* New: enable scroll when content exceeds */
}

.modal-footer {
    padding: 16px 20px;
    border-top: 1px solid #1d2452;
}

.report-dialog {
    color: white;
}

.thank-you-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 2rem 1rem;
    color: white;
}

.field {
    margin: 16px 0;
}

.field label {
    display: block;
    margin-bottom: 0.5rem;
    color: white;
    font-weight: 500;
}

.cancel-button,
.confirm-button {
    padding: 8px 16px;
    border: none;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    color: white;
    transition: opacity 0.2s;
}

.cancel-button {
    background-color: transparent;
    border: 1px solid rgba(255, 255, 255, 0.3);
}

.confirm-button {
    background-color: #00c1b1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.cancel-button:hover {
    background-color: rgba(255, 255, 255, 0.1);
}

.confirm-button:hover {
    opacity: 0.9;
}

.confirm-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.loading-spinner {
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: white;
    animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}
</style>
